import type { PostMergeTags } from '~/interfaces/post_merge_tags';

interface FetchMergeTagsParams {
  postId: string;
  isDraft: boolean;
  previewJwtToken?: string;
}

/**
 * Fetches merge tags for a post from the API
 *
 * @param postId - The ID of the post
 * @param isDraft - Whether this is a draft post
 * @returns PostMergeTags object with built_substitutions, or empty object on error
 */
export async function fetchMergeTags({
  postId,
  isDraft,
  previewJwtToken,
}: FetchMergeTagsParams): Promise<PostMergeTags> {
  try {
    const url = new URL(`/merge_tags/${postId}`, window.location.origin);
    url.searchParams.set('draft', String(isDraft));
    if (previewJwtToken) {
      url.searchParams.set('jwt_token', previewJwtToken);
    }

    const mergeTagsResponse = await fetch(url.pathname + url.search, {
      method: 'GET',
      headers: { 'Sec-Fetch-Dest': 'empty' },
    });

    // Check if response is ok before parsing
    if (!mergeTagsResponse.ok) {
      console.debug('Failed fetching merge tags: non-ok response', mergeTagsResponse.status);
      return {};
    }

    // Validate that we have a JSON response before parsing
    const contentType = mergeTagsResponse.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      console.debug('Failed fetching merge tags: non-JSON response', contentType);
      return {};
    }

    const mergeTags = await mergeTagsResponse.json();
    return ('built_substitutions' in mergeTags ? mergeTags.built_substitutions : {}) as PostMergeTags;
  } catch (error) {
    // Don't report to Sentry - this is likely a transient network issue or invalid response
    // The breadcrumb will still be visible if this causes other errors
    console.debug('Failed fetching merge tags', error);
    return {};
  }
}
